export default function carousel(opts = {}) {
    return {
        slides: [],
        active: 0,
        pageIndex: 0,
        slideWidth: 0,
        loop: opts.loop ?? true,
        autoplay: opts.autoplay ?? false,
        enable: opts.enable ?? true,
        interval: opts.interval ?? 5000,
        slidesToShow: opts.slidesToShow ?? 1,
        breakpoints: opts.breakpoints ?? {},
        disableBreakpoint: opts.disableBreakpoint ?? null,
        autoplayTimer: null,
        paused: false,
        dragging: false,
        startX: 0,
        currentX: 0,
        requestTick: false,
        mutationObserver: null,
        resizeObserver: null,

        init() {
            this.$nextTick(() => {
                this.detectSlides()
                this.updateWidth()
                this.observeLazyImages()

                // Observer for dynamic slides
                this.mutationObserver = new MutationObserver(() => {
                    this.detectSlides()
                    this.updateWidth()
                    this.observeLazyImages()
                })
                if (this.$refs.track) {
                    this.mutationObserver.observe(this.$refs.track, { childList: true, subtree: false })
                }

                // ResizeObserver for container size changes (visibility, layout shifts)
                if ('ResizeObserver' in window) {
                    this.resizeObserver = new ResizeObserver(() => {
                        this.updateWidth()
                    })
                    this.resizeObserver.observe(this.$el)
                }

                window.addEventListener('resize', () => this.handleResponsive())
                this.handleResponsive()

                const track = this.$refs.track
                if (!track) return
                ;['touchstart', 'mousedown'].forEach(ev => track.addEventListener(ev, this.onStart.bind(this)))
                ;['touchmove', 'mousemove'].forEach(ev => track.addEventListener(ev, this.onMove.bind(this)))
                ;['touchend', 'mouseup', 'mouseleave'].forEach(ev => track.addEventListener(ev, this.onEnd.bind(this)))

                if (this.autoplay && this.enable) this.startAutoplay()
            })
        },

        handleResponsive() {
            if (this.disableBreakpoint && window.innerWidth >= this.disableBreakpoint) {
                this.disableCarousel()
            } else {
                this.enableCarousel()
            }
        },

        disableCarousel() {
            this.enable = false
            this.stop()
            if (this.$refs.track) this.$refs.track.style.transform = ''
            this.slides.forEach(slide => {
                slide.style.width = 'auto'
                slide.style.flex = '1 1 auto'
            })
        },

        enableCarousel() {
            this.enable = true
            this.updateWidth()
            if (this.autoplay) this.startAutoplay()
        },

        detectSlides() {
            this.slides = Array.from(this.$refs.track?.children ?? []).filter(el => el.tagName !== 'TEMPLATE')
            this.slides.forEach(el => {
                el.classList.add('flex-shrink-0', 'overflow-hidden')
                // Evitar que el navegador arrastre la imagen en desktop
                el.querySelectorAll('img').forEach(img => {
                    img.setAttribute('draggable', 'false')
                    img.style.userSelect = 'none'
                    img.addEventListener('dragstart', e => e.preventDefault())
                })
            })
        },

        updateWidth() {
            if (!this.enable || !this.$refs.track) return

            const track = this.$refs.track
            const containerWidth = track?.parentElement?.clientWidth
            if (!track || !containerWidth) return

            const sortedBps = Object.keys(this.breakpoints)
                .map(k => parseInt(k))
                .sort((a, b) => a - b)

            let slidesCount = this.slidesToShow
            for (const bp of sortedBps) {
                if (window.innerWidth >= bp) slidesCount = this.breakpoints[bp].slidesToShow
            }

            this.slidesToShow = slidesCount
            this.slideWidth = containerWidth / this.slidesToShow

            this.slides.forEach(slide => {
                slide.style.width = `${this.slideWidth}px`
                slide.style.flex = `0 0 ${this.slideWidth}px`
            })

            this.snapToCurrent()
        },

        observeLazyImages() {
            const observer = new IntersectionObserver((entries, obs) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target
                        img.src = img.dataset.src
                        obs.unobserve(img)
                    }
                })
            }, { root: this.$el, rootMargin: '100px' })

            this.$el.querySelectorAll('img[data-src]').forEach(img => observer.observe(img))
        },

        prev() {
            if (this.active > 0) {
                this.active = Math.max(this.active - this.slidesToShow, 0)
            } else if (this.loop) {
                this.active = this.slides.length - this.slidesToShow
            }
            this.updatePageIndex()
            this.snapToCurrent()
        },

        next() {
            const maxActive = this.slides.length - this.slidesToShow
            if (this.active < maxActive) {
                this.active = Math.min(this.active + this.slidesToShow, maxActive)
            } else if (this.loop) {
                this.active = 0
            }
            this.updatePageIndex()
            this.snapToCurrent()
        },

        goTo(i) {
            this.active = i * this.slidesToShow
            this.updatePageIndex()
            this.snapToCurrent()
        },

        snapToCurrent() {
            if (!this.enable || !this.$refs.track) return
            this.$refs.track.style.transform = `translateX(-${this.active * this.slideWidth}px)`
        },

        updatePageIndex() {
            const totalPages = Math.ceil(this.slides.length / this.slidesToShow)
            this.pageIndex = Math.min(Math.floor(this.active / this.slidesToShow), totalPages - 1)
        },

        startAutoplay() {
            if (this.autoplayTimer || !this.enable) return
            this.autoplayTimer = setInterval(() => {
                if (!this.paused) this.next()
            }, this.interval)
        },

        stop() {
            clearInterval(this.autoplayTimer)
            this.autoplayTimer = null
        },

        destroy() {
            this.stop()
            if (this.mutationObserver) {
                this.mutationObserver.disconnect()
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect()
            }
        },

        pause() {
            this.paused = true
        },

        resume() {
            this.paused = false
        },

        onStart(e) {
            if (!this.enable) return

            // Prevenir drag nativo del navegador de forma radical en mousedown para imagenes
            if (e.type === 'mousedown') {
                if (e.target.tagName === 'IMG' || e.target.closest('a')) {
                    e.preventDefault(); // Detener el drag nativo antes de que empiece
                }
            }

            this.dragging = true
            this.startX = e.touches ? e.touches[0].clientX : e.clientX
            this.currentX = this.startX
            this.$refs.track.classList.remove('transition-transform')
        },

        onMove(e) {
            if (!this.dragging || !this.enable) return
            this.currentX = e.touches ? e.touches[0].clientX : e.clientX

            // Evitar comportamiento por defecto del scroll si estamos moviendo horizontalmente
            if (Math.abs(this.startX - this.currentX) > 5) {
                if (e.cancelable) e.preventDefault();
            }

            if (this.requestTick) return
            this.requestTick = true

            requestAnimationFrame(() => {
                const diff = this.startX - this.currentX
                this.$refs.track.style.transform = `translateX(calc(-${this.active * this.slideWidth}px - ${diff}px))`
                this.requestTick = false
            })
        },

        onEnd(e) {
            if (!this.dragging || !this.enable) return
            const diff = this.startX - this.currentX
            this.dragging = false
            this.$refs.track.classList.add('transition-transform')
            if (Math.abs(diff) < this.slideWidth / 4) {
                this.snapToCurrent()
                return
            }
            diff > 0 ? this.next() : this.prev()
        },
    }
}
